import React from 'react';
import { useFormContext } from '@grnhse/seedling/lib/birch/components/forms/form';

import { CompanyNameFilter } from 'talent_matching/types';
import { withLocalePrefix } from 'shared/utils/translation';
import AppliedFilterChip, {
  type AppliedFilterChipProps,
} from 'talent_matching/components/applied_filters/applied_filter_chip';
import BaseAppliedFiltersRow from 'talent_matching/components/shared/base_applied_filters_row';
import { MY_GREENHOUSE_DEFAULT_VALUES } from 'talent_matching/my_greenhouse/my_greenhouse_default_values';
import type { Item } from '@grnhse/seedling/lib/birch/components/forms/dropdown/item';

const t = withLocalePrefix('talent_matching.filters.applied');

const MyGreenhouseAppliedFilters = () => {
  const { getValues } = useFormContext();

  const [
    savedForLater,
    minExperience,
    maxExperience,
    companyNameFilters,
    canHave,
    mustHave,
    doesNotHave,
    workPreferenceLanguages,
    workPreferenceEmploymentTypes,
    workPreferenceWorksites,
    workPreferenceLocations,
    workPreferenceWillingToRelocate,
  ] = getValues([
    'savedForLater',
    'minExperience',
    'maxExperience',
    'companyNameFilters',
    'canHave',
    'mustHave',
    'doesNotHave',
    'workPreferenceLanguages',
    'workPreferenceEmploymentTypes',
    'workPreferenceWorksites',
    'workPreferenceLocations',
    'workPreferenceWillingToRelocate',
  ]);

  const filters: Omit<AppliedFilterChipProps, 'formDefaults'>[] = [];
  const defaults = MY_GREENHOUSE_DEFAULT_VALUES as Record<string, unknown>;

  if (savedForLater) {
    filters.push({
      label: t('saved_for_later'),
      filterKeys: ['savedForLater'],
      uniqueKey: 'savedForLater',
    });
  }

  if (minExperience) {
    const yearsText = t('years', { count: Number(minExperience) });
    filters.push({
      label: t('minimum_experience', { years: yearsText }),
      filterKeys: ['minExperience'],
      uniqueKey: 'minimum_experience',
    });
  }

  if (maxExperience) {
    const yearsText = t('years', { count: Number(maxExperience) });
    filters.push({
      label: t('maximum_experience', { years: yearsText }),
      filterKeys: ['maxExperience'],
      uniqueKey: 'maximum_experience',
    });
  }

  if (companyNameFilters.length > 0) {
    companyNameFilters.forEach((filter: CompanyNameFilter) => {
      if (filter.value) {
        filters.push({
          label: t(`company_name_${filter.mode}`, { company: filter.value }),
          filterKeys: [`companyNameFilters.${filter.id}`],
          uniqueKey: `companyNameFilters.${filter.id}`,
        });
      }
    });
  }

  if (canHave.length > 0) {
    filters.push({
      label: t('keywords.can_have', { keywords: canHave.join(', ') }),
      filterKeys: ['canHave'],
      uniqueKey: 'keywords.can_have',
    });
  }

  if (mustHave.length > 0) {
    filters.push({
      label: t('keywords.must_have', { keywords: mustHave.join(', ') }),
      filterKeys: ['mustHave'],
      uniqueKey: 'keywords.must_have',
    });
  }

  if (doesNotHave.length > 0) {
    filters.push({
      label: t('keywords.does_not_have', { keywords: doesNotHave.join(', ') }),
      filterKeys: ['doesNotHave'],
      uniqueKey: 'keywords.does_not_have',
    });
  }

  if ((workPreferenceLanguages as Item[])?.length > 0) {
    const labels = (workPreferenceLanguages as Item[])
      .map((i) => i.label)
      .join(', ');
    filters.push({
      label: t('work_preference_languages', { languages: labels }),
      filterKeys: ['workPreferenceLanguages'],
      uniqueKey: 'workPreferenceLanguages',
    });
  }

  if ((workPreferenceEmploymentTypes as Item[])?.length > 0) {
    const labels = (workPreferenceEmploymentTypes as Item[])
      .map((i) => i.label)
      .join(', ');
    filters.push({
      label: t('work_preference_employment_types', { types: labels }),
      filterKeys: ['workPreferenceEmploymentTypes'],
      uniqueKey: 'workPreferenceEmploymentTypes',
    });
  }

  if ((workPreferenceWorksites as Item[])?.length > 0) {
    const labels = (workPreferenceWorksites as Item[])
      .map((i) => i.label)
      .join(', ');
    filters.push({
      label: t('work_preference_worksites', { worksites: labels }),
      filterKeys: ['workPreferenceWorksites'],
      uniqueKey: 'workPreferenceWorksites',
    });
  }

  if ((workPreferenceLocations as Item[])?.length > 0) {
    const labels = (workPreferenceLocations as Item[])
      .map((i) => i.label)
      .join(', ');
    filters.push({
      label: t('work_preference_locations', { locations: labels }),
      filterKeys: ['workPreferenceLocations'],
      uniqueKey: 'workPreferenceLocations',
    });
  }

  if (workPreferenceWillingToRelocate) {
    filters.push({
      label: t('willing_to_relocate'),
      filterKeys: ['workPreferenceWillingToRelocate'],
      uniqueKey: 'workPreferenceWillingToRelocate',
    });
  }

  const chips = filters.map((filter) => (
    <AppliedFilterChip
      key={filter.uniqueKey}
      formDefaults={defaults}
      {...filter}
    />
  ));

  return <BaseAppliedFiltersRow chips={chips} />;
};

export default MyGreenhouseAppliedFilters;
